import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { useToast } from "@/components/Toast"
import { useCommsOrgs, useCreateOrg, CommsOrgRow, formatAge } from "@/hooks/useComms"
import { BuildingOffice2Icon } from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

/** Org follow-up coverage: a non-active status means members inherit "covered". */
function CoverageBadge({ status }: { status: CommsOrgRow["followup_status"] }) {
  if (status === "active") return <span className="text-xs text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
      covered · {status.replace("_", " ")}
    </span>
  )
}

export function CommsOrgs() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data, isLoading, error } = useCommsOrgs({ limit: 200 })
  const createOrg = useCreateOrg()

  const [creating, setCreating] = useState(false)
  const [name, setName] = useState("")
  const [domain, setDomain] = useState("")

  const submit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    createOrg.mutate(
      { name: trimmed, primary_domain: domain.trim() || null },
      {
        onSuccess: (org) => {
          toast({ title: "Org created", description: org.name })
          setName("")
          setDomain("")
          setCreating(false)
          navigate(`/comms/orgs/${org.public_id}`)
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to create org. Please try again.",
            variant: "destructive",
          })
        },
      }
    )
  }

  const columns = useMemo<ColumnDef<CommsOrgRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Org",
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="font-medium text-primary">{row.original.name}</p>
            {row.original.primary_domain && (
              <p className="truncate text-xs text-muted-foreground">{row.original.primary_domain}</p>
            )}
          </div>
        ),
      },
      {
        id: "members",
        header: "Members",
        cell: ({ row }) => <span className="text-sm">{row.original.member_count}</span>,
      },
      {
        id: "activity",
        header: "Last activity",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{formatAge(row.original.last_activity)}</span>
        ),
      },
      {
        id: "coverage",
        header: "Coverage",
        cell: ({ row }) => <CoverageBadge status={row.original.followup_status} />,
      },
      {
        id: "relevance",
        header: "",
        cell: ({ row }) =>
          row.original.is_relevant === false ? (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
              not relevant
            </span>
          ) : null,
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  const orgs = data?.orgs ?? []
  const total = data?.total ?? orgs.length

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">Comms · Organizations</h1>
            <p className="text-muted-foreground">
              {isLoading
                ? "Loading…"
                : `${total} ${total === 1 ? "org" : "orgs"} · email-contact groupings, not customer Organizations`}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setCreating((v) => !v)}>
            {creating ? "Cancel" : "+ New org"}
          </Button>
        </div>

        {creating && (
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-surface p-4">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Name</span>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="Texas Syndicate"
                className="rounded-md border border-border bg-background px-3 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Primary domain (optional)</span>
              <input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="texassyndicategroup.com"
                className="rounded-md border border-border bg-background px-3 py-1.5"
              />
            </label>
            <Button size="sm" onClick={submit} disabled={!name.trim() || createOrg.isPending}>
              Create
            </Button>
          </div>
        )}

        {isLoading ? (
          <TableSkeleton rows={8} />
        ) : orgs.length === 0 ? (
          <EmptyState
            icon={<BuildingOffice2Icon className="h-12 w-12" />}
            title="No organizations yet"
            description="Create an org, then assign contacts to it from their detail page."
          />
        ) : (
          <DataTable
            columns={columns}
            data={orgs}
            searchKey="name"
            onRowClick={(row) => navigate(`/comms/orgs/${row.public_id}`)}
          />
        )}
      </div>
    </Layout>
  )
}
